import { fetchWrapper } from '@/reducers/fetch'
import axios from 'axios'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ReduxState } from '@/utils/typeHelpers'
import { createSelector } from 'reselect'
import { times } from 'lodash'

export interface UserIncomePlanSource {
  id: number
  incomePlanId: number
  createdAt: string
  updatedAt: string
  name: string
  source: 'therapy' | 'non_therapy'
  rateInCents: number
  numberPerMonth?: number | null
  cadence: 'hourly' | 'monthly'
  payerType?: string | null
}

export interface UserIncomePlan {
  id: number
  createdAt: string
  updatedAt: string
  planName: string
  totalPlannedIncomeInCents: number
  therapyIncomeInCents: number
  nonTherapyIncomeInCents: number
  ratePerSession: number
  sessionsPerMonth: number
  incomePlanSources: UserIncomePlanSource[]
}

export interface UserIncomePlanMonth {
  id: number
  createdAt: string
  updatedAt: string
  notes: string
  totalPlannedIncomeInCents?: number
  therapyIncomeInCents?: number
  nonTherapyIncomeInCents?: number
  ratePerSession?: number
  sessionsPerMonth?: number
  month: number
  year: number
  pastTotalBusinessIncomeInCents?: number
  currentTotalBusinessIncomeInCents?: number
}

const initialState: {
  userIncomePlans: {
    [id: number]: UserIncomePlan
  }
  userIncomePlanMonths: {
    [yearMonth: string]: UserIncomePlanMonth
  }
} = {
  userIncomePlans: {},
  userIncomePlanMonths: {},
}

const incomePlanningSlice = createSlice({
  name: 'incomePlanning',
  initialState,
  reducers: {
    setManyIncomePlans: (state, action: PayloadAction<UserIncomePlan[]>) => {
      action.payload.forEach((plan) => {
        state.userIncomePlans[plan.id] = plan
      })
    },
    setIncomePlanMonth: (state, action: PayloadAction<UserIncomePlanMonth>) => {
      state.userIncomePlanMonths[
        `${action.payload.year}-${action.payload.month}`
      ] = {
        ...state.userIncomePlanMonths[
          `${action.payload.year}-${action.payload.month}`
        ],
        ...action.payload,
      }
    },
    setManyIncomePlanMonths: (
      state,
      action: PayloadAction<UserIncomePlanMonth[]>
    ) => {
      action.payload.forEach((month) => {
        state.userIncomePlanMonths[`${month.year}-${month.month}`] = {
          ...state.userIncomePlanMonths[`${month.year}-${month.month}`],
          ...month,
        }
      })
    },
  },
})

export default incomePlanningSlice.reducer

const { setIncomePlanMonth, setManyIncomePlanMonths, setManyIncomePlans } =
  incomePlanningSlice.actions

const selectUserIncomePlanState = (state: ReduxState) =>
  state.incomePlanning.userIncomePlanMonths

export const selectIncomePlanningMonthsForYear = createSelector(
  selectUserIncomePlanState,
  (_: unknown, year: number) => year,
  (months, year) =>
    times(12).map((i) => {
      // times is 0 indexed
      const month = i + 1

      return (
        months[`${year}-${month}`] || {
          notes: '',
          month,
          year,
        }
      )
    })
)

export const selectUserIncomePlans = (state: ReduxState) =>
  state.incomePlanning.userIncomePlans

export const selectUserIncomePlanById = createSelector(
  selectUserIncomePlans,
  (_: unknown, id: number | undefined | null) => id,
  (plans, id) => (id && plans[id] ? plans[id] : null)
)

export const fetchUserIncomePlans = () =>
  fetchWrapper({
    fetchFunction: async (dispatch) => {
      const json = await axios.get<UserIncomePlan[]>(
        '/finances/api/v1/user_income_planning/user_income_plans'
      )

      dispatch(setManyIncomePlans(json.data))

      return json.data
    },
  })

export const fetchUserIncomePlan = (id: number) =>
  fetchWrapper({
    fetchFunction: async () => {
      const json = await axios.get<UserIncomePlan>(
        `/finances/api/v1/user_income_planning/user_income_plans/${id}`
      )

      return json.data
    },
  })

export const createUserIncomePlan = (data: Partial<UserIncomePlan>) =>
  fetchWrapper({
    fetchFunction: async () => {
      const json = await axios.post<UserIncomePlan>(
        '/finances/api/v1/user_income_planning/user_income_plans',
        data
      )
      return json.data
    },
  })

export const updateUserIncomePlan = (
  id: number,
  data: Partial<UserIncomePlan>
) =>
  fetchWrapper({
    fetchFunction: async () => {
      const json = await axios.put<UserIncomePlan>(
        `/finances/api/v1/user_income_planning/user_income_plans/${id}`,
        data
      )
      return json.data
    },
  })

export const deleteUserIncomePlan = (id: number) =>
  fetchWrapper({
    fetchFunction: async () => {
      const json = await axios.delete<UserIncomePlan>(
        `/finances/api/v1/user_income_planning/user_income_plans/${id}`
      )
      return json.data
    },
  })

export const fetchUserIncomeMonthsForYear = (year: number) =>
  fetchWrapper({
    fetchFunction: async (dispatch) => {
      const json = await axios.get<{
        incomeMonths: Array<
          UserIncomePlanMonth & {
            pastTotalBusinessIncomeInCents: number
            currentTotalBusinessIncomeInCents: number
          }
        >
        pastYearTotalBusinessIncomeInCents: number
        currentYearTotalBusinessIncomeInCents: number
      }>(`/finances/api/v1/user_income_planning/user_income_months/${year}`)

      dispatch(setManyIncomePlanMonths(json.data.incomeMonths))

      return json.data
    },
  })

export const upsertUserIncomeMonth = (
  data: Partial<UserIncomePlanMonth> & { year: number; month: number }
) =>
  fetchWrapper({
    fetchFunction: async (dispatch) => {
      const json = await axios.put<UserIncomePlanMonth>(
        `/finances/api/v1/user_income_planning/user_income_months/${data.year}/${data.month}`,
        data
      )

      dispatch(setIncomePlanMonth(json.data))

      return json.data
    },
  })

export const bulkUpdateUserIncomeMonths = (data: {
  planId: number
  months: Array<{ month: number; year: number }>
}) =>
  fetchWrapper({
    fetchFunction: async (dispatch) => {
      const json = await axios.post<UserIncomePlanMonth[]>(
        '/finances/api/v1/user_income_planning/user_income_months/bulk_update',
        data
      )

      dispatch(setManyIncomePlanMonths(json.data))

      return json.data
    },
  })
